/**
 * Valida que el FormData contenga los campos requeridos para un comunicado
 * @param formData - El FormData a validar
 * @returns {boolean} - true si es válido, false si no
 */
export const validateCommunicationFormData = (
  formData: FormData
): { isValid: boolean; errors: string[] } => {
  const errors: string[] = []

  // Verificar que FormData no esté vacío
  if (!formData || formData.entries().next().done) {
    errors.push("No se han proporcionado datos")
    return { isValid: false, errors }
  }

  // Validar campos requeridos
  const title = formData.get("title")
  const message = formData.get("message")

  if (!title || (typeof title === "string" && title.trim() === "")) {
    errors.push("El título es requerido")
  }

  // if (!message || (typeof message === 'string' && message.trim() === '')) {
  //     errors.push("El mensaje es requerido");
  // }

  // Validar que al menos un canal de comunicación esté seleccionado
  const pin = formData.get("pin")
  const email = formData.get("email")
  const whatsapp = formData.get("whatsapp")
  const newsfeed = formData.get("newsfeed")

  const hasCommunicationChannel =
    pin === "true" ||
    email === "true" ||
    whatsapp === "true" ||
    newsfeed === "true"

  if (!hasCommunicationChannel) {
    errors.push("Debe seleccionar al menos un canal de comunicación")
  }

  return {
    isValid: errors.length === 0,
    errors,
  }
}

/**
 * Valida el tamaño del archivo adjunto
 * @param file - El archivo a validar
 * @param maxSizeMB - Tamaño máximo en MB (por defecto 10MB)
 * @returns {boolean} - true si es válido, false si no
 */
export const validateFileSize = (
  file: File,
  maxSizeMB: number = 10
): { isValid: boolean; error?: string } => {
  const maxSizeBytes = maxSizeMB * 1024 * 1024 // Convertir MB a bytes

  if (file.size > maxSizeBytes) {
    return {
      isValid: false,
      error: `El archivo es demasiado grande. Tamaño máximo: ${maxSizeMB}MB`,
    }
  }

  return { isValid: true }
}

/**
 * Valida el tipo de archivo
 * @param file - El archivo a validar
 * @param allowedTypes - Array de tipos MIME permitidos
 * @returns {boolean} - true si es válido, false si no
 */
export const validateFileType = (
  file: File,
  allowedTypes: string[] = [
    "image/jpeg",
    "image/png",
    "image/gif",
    "application/pdf",
  ]
): { isValid: boolean; error?: string } => {
  if (!allowedTypes.includes(file.type)) {
    return {
      isValid: false,
      error: `Tipo de archivo no permitido. Tipos permitidos: ${allowedTypes.join(", ")}`,
    }
  }

  return { isValid: true }
}

/**
 * Valida las opciones de una encuesta
 * @param options - Array de opciones a validar
 * @param minOptions - Número mínimo de opciones requeridas (default: 2)
 * @param maxLength - Longitud máxima por opción (default: 250)
 * @returns {object} - Resultado de la validación
 */
export const validateSurveyOptions = (
  options: string[],
  minOptions: number = 2,
  maxLength: number = 250
): { isValid: boolean; errors: string[]; validOptions: string[] } => {
  const errors: string[] = []

  // Filtrar opciones válidas (no vacías)
  const validOptions = options.filter((option) => option.trim() !== "")

  // Validar número mínimo de opciones
  if (validOptions.length < minOptions) {
    errors.push(`Debes agregar al menos ${minOptions} opciones válidas.`)
  }

  // Validar longitud máxima de cada opción
  const invalidLengthOptions = validOptions.filter(
    (option) => option.length > maxLength
  )
  if (invalidLengthOptions.length > 0) {
    errors.push(
      `Algunas opciones exceden el límite de ${maxLength} caracteres.`
    )
  }

  // Validar opciones duplicadas
  const uniqueOptions = new Set(validOptions)
  if (uniqueOptions.size !== validOptions.length) {
    errors.push("No puedes tener opciones duplicadas.")
  }

  // Validar que las opciones no sean solo espacios en blanco
  const whitespaceOnlyOptions = validOptions.filter((option) =>
    /^\s+$/.test(option)
  )
  if (whitespaceOnlyOptions.length > 0) {
    errors.push("Las opciones no pueden contener solo espacios en blanco.")
  }

  return {
    isValid: errors.length === 0,
    errors,
    validOptions: validOptions.filter(
      (option) => option.length <= maxLength && !/^\s+$/.test(option)
    ),
  }
}

/**
 * Valida la pregunta de una encuesta
 * @param question - La pregunta a validar
 * @param maxLength - Longitud máxima permitida (default: 250)
 * @returns {object} - Resultado de la validación
 */
export const validateSurveyQuestion = (
  question: string,
  maxLength: number = 250
): { isValid: boolean; errors: string[] } => {
  const errors: string[] = []

  if (!question || question.trim() === "") {
    errors.push("La pregunta es requerida.")
  } else if (question.length > maxLength) {
    errors.push(`La pregunta no puede exceder ${maxLength} caracteres.`)
  } else if (/^\s+$/.test(question)) {
    errors.push("La pregunta no puede contener solo espacios en blanco.")
  }

  return { isValid: errors.length === 0, errors }
}

/**
 * Valida la selección de opciones en una votación
 * @param selectedOptions - Array de IDs de opciones seleccionadas
 * @param isMultipleChoice - Si permite selección múltiple
 * @param totalOptions - Número total de opciones disponibles
 * @returns {object} - Resultado de la validación
 */
export const validatePollSelection = (
  selectedOptions: number[],
  isMultipleChoice: boolean = true,
  totalOptions: number = 0
): { isValid: boolean; errors: string[] } => {
  const errors: string[] = []

  if (selectedOptions.length === 0) {
    errors.push("Debes seleccionar al menos una opción.")
  }

  if (!isMultipleChoice && selectedOptions.length > 1) {
    errors.push("Solo puedes seleccionar una opción.")
  }

  if (selectedOptions.length > totalOptions) {
    errors.push("Has seleccionado más opciones de las disponibles.")
  }

  return { isValid: errors.length === 0, errors }
}
